import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  margin: 150px auto;
  padding: 100px 50px;
  background: linear-gradient(135deg, rgba(0, 153, 255, 0.03) 0%, rgba(0, 153, 255, 0) 100%);
  border-radius: 8px;

  @media (max-width: 768px) {
    margin: 100px 0;
    padding: 60px 30px;
  }

  @media (max-width: 480px) {
    margin: 60px 0;
    padding: 40px 20px;
  }

  .section-header {
    text-align: center;
    margin-bottom: 80px;

    .accent-line {
      font-size: var(--fz-sm);
      letter-spacing: 0.15em;
      color: var(--green);
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 20px;
    }

    h2 {
      font-size: clamp(32px, 8vw, 48px);
      font-weight: 700;
      color: var(--lightest-slate);
      margin-bottom: 20px;
      line-height: 1.2;
    }
  }

  .about-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 40px;
    }
  }
`;

const StyledTextColumn = styled.div`
  h3 {
    font-size: var(--fz-xl);
    color: var(--lightest-slate);
    margin-bottom: 20px;
    font-weight: 600;
  }

  p {
    font-size: var(--fz-md);
    color: var(--slate);
    line-height: 1.8;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: var(--fz-sm);
    }
  }
`;

const StyledStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const StyledStatCard = styled.div`
  padding: 30px;
  background-color: rgba(17, 34, 51, 0.6);
  border: 1px solid rgba(0, 153, 255, 0.2);
  border-radius: 8px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--green);
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 153, 255, 0.1);
  }

  .stat-number {
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 800;
    color: var(--green);
    line-height: 1;
    margin-bottom: 10px;
  }

  .stat-label {
    font-size: var(--fz-sm);
    color: var(--light-slate);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }
`;

const StyledValuesList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 30px;

  li {
    font-size: var(--fz-md);
    color: var(--slate);
    margin-bottom: 16px;
    padding-left: 30px;
    position: relative;
    line-height: 1.6;

    &:before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--green);
      font-weight: 800;
      font-size: var(--fz-lg);
    }

    @media (max-width: 768px) {
      font-size: var(--fz-sm);
    }
  }
`;

const AboutBusiness = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <div className="section-header">
        <div className="accent-line">Who We Are</div>
        <h2>Connecting Talent with Technology</h2>
      </div>

      <div className="about-content">
        <StyledTextColumn>
          <h3>Our Mission</h3>
          <p>
            At Draa Talents, we bridge the gap between innovative businesses and exceptional IT
            talent. We specialize in providing cutting-edge solutions in cloud infrastructure, AI
            technology, data engineering, and full-stack development.
          </p>
          <p>
            With a focus on quality, innovation, and client success, we empower organizations to
            transform digitally and stay ahead in a rapidly evolving tech landscape.
          </p>

          <StyledValuesList>
            <li>Expert talent in emerging technologies</li>
            <li>Client-focused solutions and partnerships</li>
            <li>Innovation-driven approach to IT consulting</li>
            <li>Proven track record of successful deployments</li>
          </StyledValuesList>
        </StyledTextColumn>

        <div>
          <StyledStatsGrid>
            <StyledStatCard>
              <div className="stat-number">10+</div>
              <div className="stat-label">Years Experience</div>
            </StyledStatCard>
            <StyledStatCard>
              <div className="stat-number">50+</div>
              <div className="stat-label">Projects Delivered</div>
            </StyledStatCard>
            <StyledStatCard>
              <div className="stat-number">100%</div>
              <div className="stat-label">Client Satisfaction</div>
            </StyledStatCard>
            <StyledStatCard>
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support Available</div>
            </StyledStatCard>
          </StyledStatsGrid>

          <StyledTextColumn style={{ marginTop: '40px' }}>
            <h3>Why Choose Us?</h3>
            <p>
              We understand that technology is only as good as the people behind it. That's why we
              carefully match skilled professionals with your unique business needs, ensuring
              seamless integration and exceptional results.
            </p>
          </StyledTextColumn>
        </div>
      </div>
    </StyledAboutSection>
  );
};

export default AboutBusiness;
